import React from 'react';
import { CheckIcon, MoonStarIcon, ShieldCheckIcon, SparklesIcon, ZapIcon } from 'lucide-react';
import { CtaButton } from '../components/CtaButton';
import { FaqItem } from '../components/FaqItem';
import { bonuses, faqs, features, heroIncludes, offerChecklist, outcomes, painPoints, volumeTitles } from '../data/content';
const EBOOK_COVER_URL = 'https://cdn.magicpatterns.com/uploads/6bVWAGwAHJ7VNCabcqAiXr/Mockup_(1).png';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/1roViPmDXjaiz1sGkRNVUW/Logo_IG.jpg';
const phases = [{
  label: 'Intake',
  description: 'Forms and session flow that get you ready in 15 minutes.'
}, {
  label: 'Interpret',
  description: 'Sun, Moon, and Rising templates for every scenario.'
}, {
  label: 'Deepen',
  description: 'Money and relationships read with context, not generalities.'
}, {
  label: 'Predict',
  description: 'Transits and returns with clear timing frameworks.'
}, {
  label: 'Relate',
  description: 'Synastry and composite charts with ready-made scripts.'
}, {
  label: 'Guide',
  description: 'Vocation and purpose mapped into a concrete roadmap.'
}];
export function SalesPage() {
  return <div className="min-h-screen w-full overflow-x-hidden bg-brand-cream font-body text-slate-800" data-id="element-10" data-mp="35qx35zi26osm">

      {/* Top banner */}
      <header data-id="element-11" data-mp="2vutzgp7k6i3k">
        <div className="bg-brand-medium px-4 py-2 text-center text-xs font-bold tracking-wide text-white sm:text-sm" data-id="element-12" data-mp="2vvc6mgvregq9">
          <span aria-hidden="true" data-id="element-13" data-mp="3thjcrv7poh4q">✨ </span>Digital download — Instant access after purchase
        </div>
        
      </header>

      <main id="top" data-id="element-14" data-mp="3okapjg7phmwm">

        {/* Hero */}
        <section className="bg-brand-deep px-5 py-8 text-center text-white sm:py-14 lg:py-16" data-id="element-15" data-mp="3jjvfoymmuxje">
          <div className="mx-auto max-w-4xl" data-id="element-16" data-mp="3fj32xvejyaid">
            <h1 className="font-display text-4xl font-bold leading-[1.08] tracking-tight sm:text-5xl lg:text-7xl" data-id="element-17" data-mp="3fi9t2lkar4w0">
              From hours of preparation to sessions of{' '}
              <span className="text-brand-gold" data-id="element-18" data-mp="242l7nymoydxb">immediate impact</span>
            </h1>
            <p className="mx-auto mt-3 max-w-2xl text-base leading-relaxed text-white/85 sm:mt-4 sm:text-xl" data-id="element-19" data-mp="25kjvsl8vnc1a">
              Cut your prep time to 15 minutes and walk into every consultation with exact answers.{' '}
              <strong className="font-black text-white" data-id="element-20" data-mp="1vlm1t2somqaw">Astrologer Ready</strong> turns your astrology into a high-level consultancy.
            </p>
            <img src={EBOOK_COVER_URL} alt="Astrologer Ready — the complete 6-volume system plus 5 bonuses included" className="mx-auto mt-3 w-full max-w-[280px] bg-transparent object-contain sm:mt-5 sm:max-w-md lg:max-w-lg" data-id="element-21" data-mp="20njqwdnrkfbr" />
            <div className="mt-4 sm:mt-6" data-id="element-22" data-mp="1cwo11m2vbu75">
              <CtaButton label="YES, I WANT ASTROLOGER READY!" data-id="element-23" data-mp="1cxz5lo4515zb" />
            </div>

            {/* What you get */}
            <div className="mx-auto mt-8 max-w-2xl text-left sm:mt-10" data-id="element-24" data-mp="1ht4rimxjikne">
              <p className="text-center text-sm font-black uppercase tracking-[0.16em] text-brand-gold" data-id="element-25" data-mp="13vznstlatxvb">
                Here is everything you get
              </p>
              <ul className="mt-4 grid gap-2 sm:grid-cols-2" role="list" data-id="element-26" data-mp="p894nyvytpvl">
                {heroIncludes.map(item => <li key={item} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3" data-id="element-27" data-mp="kafwx1f0knt7">
                    <CheckIcon className="mt-0.5 h-5 w-5 flex-none text-brand-gold" aria-hidden="true" data-id="element-28" data-mp="ya8iyjflifuu" />
                    <span className="text-[15px] leading-snug text-white/90" data-id="element-29" data-mp="t8syotl8sddi">{item}</span>
                  </li>)}
              </ul>

              <p className="mt-7 text-center text-sm font-black uppercase tracking-[0.16em] text-brand-gold" data-id="element-30" data-mp="185aa1ktfls02">
                The 6 volumes inside
              </p>
              <ul className="mt-4 space-y-2" role="list" data-id="element-31" data-mp="1gzf6cjrc3vmz">
                {volumeTitles.map(volume => <li key={volume} className="flex items-start gap-3 border-l-2 border-brand-gold/60 pl-4 text-[15px] leading-snug text-white/90" data-id="element-32" data-mp="1czejqbu30e4u">
                    {volume}
                  </li>)}
              </ul>
            </div>
          </div>
        </section>

        {/* Social proof bar */}
        <section aria-label="Social proof" className="bg-[#E6DCF3] px-5 py-4 text-center text-sm font-bold sm:text-base text-brand-deep" data-id="element-33" data-mp="26031bz1d7nz7">
          <span aria-hidden="true" data-id="element-34" data-mp="2a0n35fwvvfro">🔮 </span>Built for astrologers who want to charge like professionals, not improvise every session
        </section>

        {/* Pain points */}
        <section id="pain-points" className="bg-brand-cream px-5 py-12 sm:py-16" data-id="element-35" data-mp="2js131osqyhlc">
          <div className="mx-auto max-w-5xl" data-id="element-36" data-mp="1n3lf1x5acgac">
            <div className="mx-auto max-w-2xl text-center" data-id="element-37" data-mp="1r3vkn8p614oo">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-38" data-mp="1r4dp2slzep7o">
                You are not alone
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-39" data-mp="211rulx6d9tl7">
                Does any of this sound familiar?
              </h2>
            </div>
            <ul className="mt-8 grid gap-4 sm:grid-cols-2 sm:gap-5" role="list" data-id="element-40" data-mp="3ch8713ayzzdz">
              {painPoints.map(point => <li key={point.text} className="flex items-start gap-4 rounded-2xl border border-brand-medium/15 bg-white p-5 shadow-sm" data-id="element-41" data-mp="10tlduvv6tii6">
                  <span className="mt-0.5 text-2xl" aria-hidden="true" data-id="element-42" data-mp="vw326xpscp6h">{point.emoji}</span>
                  <span className="text-base leading-relaxed text-slate-700" data-id="element-43" data-mp="r309sswqvlrv">{point.text}</span>
                </li>)}
            </ul>
            <blockquote className="mx-auto mt-10 max-w-3xl border-l-4 border-brand-medium pl-5 font-display text-xl font-bold italic leading-relaxed text-brand-deep sm:text-2xl" data-id="element-44" data-mp="m2w13p22vvc7">
              "You do not have an astrology problem. You are missing the operating system for your practice."
            </blockquote>
            <p className="mx-auto mt-5 max-w-3xl text-center text-base leading-relaxed text-slate-600" data-id="element-45" data-mp="870lpt6w12dk">
              Most astrologers prepare every session from scratch, improvise the close, and rely on memory to interpret. That is not depth — those are unbilled hours. What separates a profitable practice from a second job is having the process written down once.
            </p>
          </div>
        </section>

        {/* Solution */}
        <section id="solution" className="bg-brand-deep px-5 py-12 text-white sm:py-16" data-id="element-46" data-mp="ly8stj2ezkq">
          <div className="mx-auto max-w-3xl text-center" data-id="element-47" data-mp="3i2ocbjvskwkl">
            <MoonStarIcon className="mx-auto h-10 w-10 text-brand-gold" aria-hidden="true" data-id="element-48" data-mp="3i03d72mvouqj" />
            <p className="mt-5 text-sm font-black uppercase tracking-[0.16em] text-brand-gold" data-id="element-49" data-mp="3mz6goin7mswe">The solution</p>
            <h2 className="mt-3 font-display text-4xl font-bold leading-tight tracking-tight sm:text-5xl" data-id="element-50" data-mp="ejms3wxhhyno">
              You don&apos;t need to work more hours. You need a documented system.
            </h2>
            <div className="mt-5 space-y-4 text-lg leading-relaxed text-white/85" data-id="element-51" data-mp="9mcrdl4na0kj">
              <p data-id="element-52" data-mp="9ofpksfyb00c">
                Astrologer Ready is not another astrology course. It is a 6-volume operating manual for your practice: the forms that structure the consultation, the interpretations you no longer have to write, and the scripts that carry every difficult conversation.
              </p>
              <p data-id="element-53" data-mp="2muvyjm7cx58q">
                <strong className="font-bold text-white" data-id="element-54" data-mp="2hu86cx7e6icx">Set it up once</strong> and every future client gets the same professional experience — without you losing another afternoon preparing a chart.
              </p>
            </div>
            <div className="mt-8 grid gap-3 text-left sm:grid-cols-2" data-id="element-55" data-mp="2rt743kqmctl5">
              {phases.map(phase => <div key={phase.label} className="rounded-xl border border-white/15 bg-white/5 p-4" data-id="element-56" data-mp="243mn37e106ei">
                  <p className="text-sm font-black uppercase tracking-wide text-brand-gold" data-id="element-57" data-mp="1z4jfbf6nvm2u">{phase.label}</p>
                  <p className="mt-1 text-sm leading-relaxed text-white/80" data-id="element-58" data-mp="2dt65kbxtty8u">{phase.description}</p>
                </div>)}
            </div>
          </div>
        </section>

        {/* What's inside */}
        <section id="inside" className="bg-[#EADFF5] px-5 py-12 sm:py-16" data-id="element-59" data-mp="1lasfiyiqfgkb">
          <div className="mx-auto max-w-6xl" data-id="element-60" data-mp="1gds9gnlvudt7">
            <div className="mx-auto max-w-2xl text-center" data-id="element-61" data-mp="1gawdh4tlhz7e">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-62" data-mp="uupdz4xl85p4">
                Fill-in-the-blank templates, ready to use
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-63" data-mp="14s3iq7a5f61l">
                The 6 Volumes of the System
              </h2>
            </div>
            <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-id="element-64" data-mp="1dsjnrl6jil8k">
              {features.map((feature, index) => <article key={feature.title} className="rounded-2xl border border-brand-medium/15 bg-white p-5 shadow-sm" data-id="element-65" data-mp="1ir385h5hjoyt" data-mpi={index}>
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand-medium font-display text-lg font-bold text-white" data-id="element-66" data-mp="1nje9mofvjith">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <h3 className="mt-5 font-display text-xl font-bold leading-snug text-brand-deep" data-id="element-67" data-mp="1wgycu2xarp6q">
                    {feature.title}
                  </h3>
                  <p className="mt-2 text-xs font-black uppercase tracking-wide text-brand-medium" data-id="element-68" data-mp="3m17vgubevs03">
                    {feature.pain}
                  </p>
                  <p className="mt-3 text-[15px] leading-relaxed text-slate-600" data-id="element-69" data-mp="3czguu3vqabd3">
                    {feature.description}
                  </p>
                </article>)}
            </div>
          </div>
        </section>

        {/* Bonuses */}
        <section id="bonuses" className="bg-[#F3EDFB] px-5 py-12 sm:py-16" data-id="element-70" data-mp="8jyv2qqn2g28">
          <div className="mx-auto max-w-5xl" data-id="element-71" data-mp="4j5aa68hthdk">
            <div className="mx-auto max-w-2xl text-center" data-id="element-72" data-mp="4kgkoowg1ndn">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-73" data-mp="3vxjf65dbzk4k">
                Included at no extra cost
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-74" data-mp="3qxf4ivz3tjm9">
                🎁 5 Exclusive Bonuses Included
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-slate-700" data-id="element-75" data-mp="nh818qklpoe0">
                Everything you need to deliver a professional-grade consultation starting today.
              </p>
            </div>
            <div className="mt-8 grid gap-4 md:grid-cols-2" data-id="element-76" data-mp="2wh9iexrc1rmw">
              {bonuses.map(bonus => <article key={bonus.title} className="rounded-r-2xl border-l-4 border-brand-medium bg-white px-5 py-5 shadow-sm sm:px-6" data-id="element-77" data-mp="3aghirtz8f5so">
                  <div className="flex items-start gap-4" data-id="element-78" data-mp="3kd42gfftnqt4">
                    <span className="text-3xl" aria-hidden="true" data-id="element-79" data-mp="3kbja8wgzcjyf">{bonus.emoji}</span>
                    <div data-id="element-80" data-mp="3fd7ygg74jzdk">
                      <span className="inline-block rounded-full bg-brand-gold px-3 py-1 text-xs font-black uppercase tracking-wide text-brand-deep" data-id="element-81" data-mp="3fbwwmlx8p1p3">
                        {bonus.tag}
                      </span>
                      <h3 className="mt-3 font-display text-xl font-bold leading-snug text-brand-deep" data-id="element-82" data-mp="3p66q66xx461e">
                        {bonus.title}
                      </h3>
                      <p className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-83" data-mp="20pivkt17eav">
                        {bonus.description}
                      </p>
                    </div>
                  </div>
                </article>)}
            </div>
          </div>
        </section>

        {/* Author */}
        <section id="author" className="bg-brand-cream px-5 py-12 sm:py-16" data-id="element-84" data-mp="cjykkmzh9l2z">
          <div className="mx-auto flex max-w-4xl flex-col items-center gap-7 text-center md:flex-row md:text-left" data-id="element-85" data-mp="gks4z6dpohr4">
            <img src={LOGO_URL} alt="Prof. Kyra logo" className="h-40 w-40 flex-none rounded-full border-8 border-white bg-black object-contain shadow-lg" data-id="element-86" data-mp="2su8pw53mgcd" />
            <div data-id="element-87" data-mp="3p2559lswqmep">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-88" data-mp="3p2obw6d3xodf">
                Created with care
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-89" data-mp="3tydfqlw5trfj">
                Meet Prof. Kyra
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-slate-700" data-id="element-90" data-mp="3kyqfi3f62ro2">
                Prof. Kyra creates practical, field-tested digital guides designed to solve real problems. Every resource is built to hand you concrete tools — not just information.
              </p>
              <p className="mt-4 text-lg leading-relaxed text-slate-700" data-id="element-91" data-mp="1gt8g07gyxm5n">
                Astrologer Ready was created for astrologers who want an organized, profitable, professional practice instead of entire afternoons spent preparing a single chart.
              </p>
            </div>
          </div>
        </section>

        {/* Outcomes */}
        <section id="outcomes" className="bg-[#EADFF5] px-5 py-12 sm:py-16" data-id="element-92" data-mp="24kdxss6gyoil">
          <div className="mx-auto max-w-4xl text-center" data-id="element-93" data-mp="20go6huyetstv">
            <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-94" data-mp="1vj66iu2deny0">
              What you will achieve
            </p>
            <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-95" data-mp="p4f2wilv4p0a">
              After the System
            </h2>
            <p className="mx-auto mt-5 max-w-3xl font-display text-xl font-bold italic leading-relaxed text-brand-deep" data-id="element-96" data-mp="2vrmgrsxszobh">
              "A great reading is not improvised. It is the result of a system written down once."
            </p>
            <ul className="mt-8 grid gap-4 text-left sm:grid-cols-2" role="list" data-id="element-97" data-mp="34qzifzfor8ln">
              {outcomes.map(outcome => <li key={outcome} className="flex items-start gap-3 rounded-2xl border border-brand-medium/15 bg-white p-5 shadow-sm" data-id="element-98" data-mp="39k2ag34te9zq">
                  <CheckIcon className="mt-0.5 h-5 w-5 flex-none text-brand-medium" aria-hidden="true" data-id="element-99" data-mp="3jgotqnhhsszn" />
                  <span className="text-base leading-relaxed text-slate-700" data-id="element-100" data-mp="3og1gz9ca022r">{outcome}</span>
                </li>)}
            </ul>
          </div>
        </section>

        {/* Offer */}
        <section id="offer" className="bg-brand-deep px-5 py-12 sm:py-16" data-id="element-101" data-mp="3sf0tpg49134r">
          <div className="mx-auto max-w-xl rounded-3xl bg-white p-7 text-center shadow-2xl sm:p-8" data-id="element-102" data-mp="1u3k14mtro485">
            <SparklesIcon className="mx-auto h-9 w-9 text-brand-medium" aria-hidden="true" data-id="element-103" data-mp="1y3m2euvlavmw" />
            <h2 className="mt-4 font-display text-3xl font-bold text-brand-deep sm:text-4xl" data-id="element-104" data-mp="12rkzrcmih9gx">
              Everything You Get Today
            </h2>
            <p className="mt-2 font-display text-2xl font-bold text-brand-deep sm:text-3xl" data-id="element-105" data-mp="1cr3i5omrwn6v">
              Astrologer Ready
            </p>
            <ul className="mt-7 space-y-3 text-left" role="list" data-id="element-106" data-mp="17ss4t3x9rujw">
              {offerChecklist.map(item => <li key={item} className="flex items-start gap-3 text-[15px] leading-snug text-slate-700" data-id="element-107" data-mp="k3fz4e15i4mc">
                  <CheckIcon className="mt-0.5 h-5 w-5 flex-none text-brand-medium" aria-hidden="true" data-id="element-108" data-mp="f564c4jc9619" />
                  <span data-id="element-109" data-mp="ttsgjxdo5ho9">{item}</span>
                </li>)}
            </ul>
            <div className="mt-8 flex flex-col items-center border-t border-brand-medium/15 pt-7 text-center" data-id="element-110" data-mp="p0pv6cj1pej5">
              <p className="text-base font-bold text-slate-500 line-through" data-id="element-111" data-mp="1avhjsqwjlxu">99 USD</p>
              <p className="mt-1 font-display text-5xl font-bold leading-none text-brand-deep" data-id="element-112" data-mp="3shg8zdkvcvve">
                27 <span className="align-middle text-2xl" data-id="element-113" data-mp="27zdzz5443x17">USD</span>
              </p>
              <p className="mt-2 text-sm font-bold text-slate-600" data-id="element-114" data-mp="2crfaalhpzdol">
                One-time payment — Lifetime access
              </p>
            </div>
            <p className="mt-6 text-sm font-black text-brand-medium" data-id="element-115" data-mp="1yxo15e7whvte">
              Launch offer · Price goes up soon
            </p>
            <CtaButton label="YES, I WANT ASTROLOGER READY!" className="mt-3 w-full" target="checkout" data-id="element-116" data-mp="2qqeyxt58fskr" />
            <p className="mt-4 text-xs font-bold tracking-wide text-slate-500" data-id="element-117" data-mp="2vqjftka1ypbi">
              🔒 Secure checkout <span aria-hidden="true" data-id="element-118" data-mp="2hqrypao8osta">|</span> 📩 Instant delivery <span aria-hidden="true" data-id="element-119" data-mp="2hscrauqzu1oi">|</span> 🛡️ 7-day guarantee
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="bg-brand-cream px-5 py-12 sm:py-16" data-id="element-120" data-mp="16bd2wa7ro6zr">
          <div className="mx-auto max-w-3xl" data-id="element-121" data-mp="16dfyd9rousbv">
            <div className="text-center" data-id="element-122" data-mp="1bb7pw2of8cfj">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-123" data-mp="1b83fgnkd4kgf">
                Clear answers
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-124" data-mp="37zzc98xfa0gp">
                Frequently Asked Questions
              </h2>
            </div>
            <div className="mt-8 space-y-3" data-id="element-125" data-mp="2z8gy13k1duq9">
              {faqs.map(faq => <FaqItem key={faq.question} {...faq} data-id="element-126" data-mp="2z5upp1pbnb6z" />)}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section id="final-cta" className="bg-brand-deep px-5 py-12 text-center text-white sm:py-16" data-id="element-127" data-mp="2pa146stmk7d2">
          <div className="mx-auto max-w-3xl" data-id="element-128" data-mp="2ka506tkxgfxi">
            <ZapIcon className="mx-auto h-10 w-10 text-brand-gold" aria-hidden="true" data-id="element-129" data-mp="2kb6ldqvnqisy" />
            <h2 className="mt-5 font-display text-4xl font-bold tracking-tight sm:text-5xl" data-id="element-130" data-mp="2gaebqwivibw5">
              Stop improvising. Work with a system.
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-white/85" data-id="element-131" data-mp="eh6pf34ueu0r">
              If you implement the system and it does not cut your prep time or improve the quality of your consultations, we refund 100% of your investment. No questions. No hassle.
            </p>
            <p className="mt-5 text-sm font-black text-brand-gold" data-id="element-132" data-mp="265eh2vb1imij">
              Now with the complete 6×1 system!
            </p>
            <CtaButton label="YES, I WANT ASTROLOGER READY!" className="mt-3" data-id="element-133" data-mp="2a5okq1bd10u8" />
          </div>
        </section>

      </main>

      <footer className="bg-[#1D1233] px-5 py-10 text-center text-white/70" data-id="element-134" data-mp="1diblaaxr0pvz">
        <ShieldCheckIcon className="mx-auto h-6 w-6 text-brand-gold" aria-hidden="true" data-id="element-135" data-mp="1igljyviom8mc" />
        <p className="mt-4 text-sm" data-id="element-136" data-mp="1mg41rif7i3ky">© 2026 Astrologer Ready by Prof. Kyra · profkyra@gmail.com</p>
        <nav aria-label="Footer navigation" className="mt-3 flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm" data-id="element-137" data-mp="1rgi90y8zh9hx">
          <a href="#privacy" className="underline-offset-4 hover:text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" data-id="element-138" data-mp="2xvixyjzlcrf0">
            Privacy Policy
          </a>
          <a href="#terms" className="underline-offset-4 hover:text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" data-id="element-139" data-mp="2ejd236gx1e64">
            Terms & Conditions
          </a>
          <a href="mailto:profkyra@gmail.com" className="underline-offset-4 hover:text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" data-id="element-140" data-mp="2ncgd78439exl">
            Contact
          </a>
        </nav>
        <p className="mx-auto mt-6 max-w-3xl text-xs leading-relaxed text-white/55" data-id="element-141" data-mp="2sa8g0x69ulgk">
          This product is an educational and informational resource for astrologers. It does not constitute legal, financial, or medical advice.
        </p>
      </footer>

    </div>;
}